import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { AuthService } from './auth.service';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  UserListRequest,
  AddOrUpdatePermissionsRequest,
  AddOrUpdateRolesRequest,
  AddOrUpdateStatesRequest,
} from '../interfaces/admin.interface';
import { map, catchError } from 'rxjs/operators';

/**
 * Servicio para gestionar funcionalidades administrativas como usuarios, roles y permisos
 */
@Injectable({
  providedIn: 'root',
})
export class AdminService {
  private apiUrl = environment.int365_dom + '/api/v2';
  private apiUrl2 = environment.int365_dom + '/api/v1';
  private adminRoleKey = 'isAdmin';
  private adminRoleSubject = new BehaviorSubject<boolean>(false);
  adminRole$ = this.adminRoleSubject.asObservable();
  private producerNameKey = 'producerName';
  menuPermissionsList = signal<{externalId: string, description: string}[]>([]);


  constructor(private http: HttpClient, private authService: AuthService) {
    // Inicializar el BehaviorSubject con el valor actual
    const isAdmin = sessionStorage.getItem(this.adminRoleKey) === 'true';
    this.adminRoleSubject.next(isAdmin);
  }

  /**
   * Obtiene la lista de roles del sistema
   * @returns Observable que emite la respuesta de la API con la lista de roles
   * @throws Error con status 404 si no se encuentran roles
   * @throws Error genérico si ocurre algún otro problema en la petición
   */
  getRoles(): Observable<any> {
    return this.http
      .get<any>(`${this.apiUrl2}/Roles`, this.getHttpOptions())
      .pipe(
        map((response) => {
          try {
            return response;
          } catch (error) {
            console.error('Error al obtener roles:', error);
            if (response.statusCode === 404) {
              throw { status: 404, error: response };
            }
            throw error;
          }
        })
      );
  }

  /**
   * Obtiene la lista de usuarios y actualiza el estado de administrador del usuario actual
   * @param request Parámetros para obtener la lista de usuarios
   * @returns Observable con la respuesta de la API conteniendo la lista de usuarios
   */
  getUsers(request: UserListRequest): Observable<any> {
    return this.http
      .get<any>(`${this.apiUrl}/Users`, this.getHttpOptions(request))
      .pipe(
        map((response) => {
          if (response.data?.users) {
            const currentUserEmail = this.authService.getEmail();
            const currentUser = response.data.users.find(
              (user: any) => user.email === currentUserEmail
            );
            if (currentUser) {
              localStorage.setItem(this.producerNameKey, currentUser.producerName);

              const isAdmin = currentUser.roles.some(
                (role: any) => role.name === 'Administrador'
              );
              sessionStorage.setItem(this.adminRoleKey, String(isAdmin));
              this.adminRoleSubject.next(isAdmin); // Emitir el nuevo valor
            }
          }
          return response;
        })
      );
  }

  /**
   * Crea un nuevo usuario en el sistema
   * @param request Datos del usuario a crear
   * @returns Observable con la respuesta de la API tras crear el usuario
   */
  createUser(request: any): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}/Users`, request, this.getHttpOptions())
      .pipe(
        map((response) => {
          try {
            return response;
          } catch (error) {
            console.error('Error en la creación de usuario:', error);
            if (response.statusCode === 404) {
              throw { status: 404, error: response };
            }
            throw error;
          }
        })
      );
  }

  /**
   * Actualiza los permisos de un usuario
   * @param request Datos de los permisos a actualizar
   * @returns Observable con la respuesta de la API tras actualizar los permisos
   */
  addOrUpdatePermissions(
    request: AddOrUpdatePermissionsRequest
  ): Observable<any> {
    return this.http
      .put<any>(
        `${this.apiUrl}/Users/Permissions`,
        request,
        this.getHttpOptions()
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) => {
          console.error('Error al actualizar permisos:', error);
          throw error;
        })
      );
  }

  /**
   * Actualiza los roles de un usuario
   * @param request Datos de los roles a actualizar
   * @returns Observable con la respuesta de la API tras actualizar los roles
   */
  addOrUpdateRoles(request: AddOrUpdateRolesRequest): Observable<any> {
    return this.http
      .put<any>(`${this.apiUrl}/Users/Roles`, request, this.getHttpOptions())
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) => {
          console.error('Error al actualizar roles:', error);
          throw error;
        })
      );
  }

  /**
   * Actualiza el estado de un usuario
   * @param request Datos del estado a actualizar
   * @returns Observable con la respuesta de la API tras actualizar el estado
   */
  addOrUpdateStates(request: AddOrUpdateStatesRequest): Observable<any> {
    return this.http
      .put<any>(`${this.apiUrl}/Users/Status`, request, this.getHttpOptions())
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) => {
          console.error('Error al actualizar estados:', error);
          throw error;
        })
      );
  }

  /**
   * Configura las opciones HTTP incluyendo headers y parámetros de autenticación
   * @param params Parámetros opcionales para la petición
   * @returns Objeto con las opciones HTTP configuradas
   * @throws Error si el usuario no está autenticado o no hay token
   */
  private getHttpOptions(params?: any) {
    if (!this.authService.isAuthenticated()) {
      throw new Error('Usuario no autenticado');
    }

    const token = this.authService.getToken();
    if (!token) {
      throw new Error('No token found');
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return {
      headers,
      params: params
        ? new HttpParams({ fromObject: this.formatParams(params) })
        : undefined,
    };
  }

  /**
   * Formatea los parámetros para las peticiones HTTP
   * @param params Objeto con los parámetros a formatear
   * @returns Objeto con los parámetros formateados como strings
   */
  private formatParams(params: any): { [key: string]: string } {
    const formattedParams: { [key: string]: string } = {};

    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined) return;

      if (value instanceof Date) {
        formattedParams[key] = value.toISOString().split('T')[0];
      } else {
        formattedParams[key] = String(value);
      }
    });

    return formattedParams;
  }

  /**
   * Establece el rol de administrador en el almacenamiento de sesión
   * @param isAdmin Booleano indicando si el usuario es administrador
   */
  setAdminRole(isAdmin: boolean) {
    sessionStorage.setItem('isAdmin', isAdmin.toString());
    this.adminRoleSubject.next(isAdmin);
  }

  /**
   * Verifica si el usuario actual tiene rol de administrador
   * @returns Booleano indicando si el usuario es administrador
   */
  isAdmin(): boolean {
    return sessionStorage.getItem('isAdmin') === 'true';
  }
}
